/**
 * Spec 031 P2.1: shared helpers for collapsible side panes — the text-field
 * focus guard behind the `[` / `]` shortcuts and guarded `qmc:*`
 * open/closed storage. Every localStorage access is try/catch-guarded so a
 * blocked or throwing store degrades to in-memory state instead of crashing.
 */

/** The one localStorage key prefix of the app (spec 004 D7); every persisted pref key is built with it. */
export const STORAGE_PREFIX = 'qmc:'

export type PanePref = 'open' | 'closed'

/**
 * True when `element` is a text-entry field the pane shortcuts must not
 * interrupt: the composer's `<textarea>`, the signal terminal's command
 * `<input>`, any `<select>`, or a `contenteditable` region.
 */
export function isTextFieldFocused(element: Element | null): boolean {
  if (element === null) return false
  const tag = element.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  const contentEditableAttr = element.getAttribute('contenteditable')
  if (contentEditableAttr === 'true' || contentEditableAttr === '') return true
  return Boolean(element instanceof HTMLElement && element.isContentEditable)
}

/** Reads a stored pref; anything other than `'open' | 'closed'` (or a throwing store) is unset. */
export function readPref(key: string): PanePref | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(key)
    return raw === 'open' || raw === 'closed' ? raw : null
  } catch {
    return null
  }
}

/** Writes a pref; a throwing store is swallowed (state stays in memory). */
export function writePref(key: string, value: PanePref): void {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(key, value)
  } catch {
    // Storage unavailable — the pane keeps working from in-memory state.
  }
}

/** Guarded raw string read for a non-open/closed `qmc:*` pref (e.g. a JSON map). Throwing store = unset. */
export function readRawPref(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Guarded raw write (`null` removes the key). Returns false when the store threw. */
export function writeRawPref(key: string, value: string | null): boolean {
  try {
    if (typeof window === 'undefined') return false
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}
